import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Check, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import { ClickSplit } from "../components/click-split";

type Interval = "hour" | "day" | "month";

interface DailyRow { day: string; sent: number; delivered: number; bounced: number; failed: number; opened: number; clicked: number }
interface ReportsData {
  daily: DailyRow[];
  allTime: { sent: number; delivered: number; bounced: number; failed: number; opened: number; clicked: number; unsubscribed: number };
  interval: Interval;
  windowDays: number;
  scope: "workspace" | "user";
  brevoWebhookSeemsUnconfigured: boolean;
}

interface ActivityRow {
  send_job_id: string; email: string; sent_at: string; status: string;
  campaign_id: string; campaign_name: string;
  delivered: boolean; opened: boolean; clicked: boolean; bounced: boolean; unsubscribed: boolean;
}

const PAGE_SIZE = 25;

export default function ReportsPage() {
  const [interval, setInterval] = useState<Interval>("day");
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "daily", interval],
    queryFn: () => api<ReportsData>(`/api/reports/daily?interval=${interval}`),
  });

  if (isLoading || !data) return <div className="p-8">Loading…</div>;

  // Build a continuous series (fill gaps with zeros) so the chart has a
  // constant column count regardless of how busy the period was.
  const rows: DailyRow[] = (() => {
    const out: DailyRow[] = [];
    const now = new Date();
    if (interval === "hour") {
      // last 48 hours
      for (let i = 47; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCHours(d.getUTCHours() - i, 0, 0, 0);
        const key = d.toISOString().slice(0, 13) + ":00"; // YYYY-MM-DDTHH:00
        const found = data.daily.find((r) => r.day === key);
        out.push(found ?? { day: key, sent: 0, delivered: 0, bounced: 0, failed: 0, opened: 0, clicked: 0 });
      }
    } else if (interval === "month") {
      // last 12 months
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCMonth(d.getUTCMonth() - i, 1);
        const key = d.toISOString().slice(0, 7); // YYYY-MM
        const found = data.daily.find((r) => r.day === key);
        out.push(found ?? { day: key, sent: 0, delivered: 0, bounced: 0, failed: 0, opened: 0, clicked: 0 });
      }
    } else {
      // last 30 days
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10); // YYYY-MM-DD
        const found = data.daily.find((r) => r.day === key);
        out.push(found ?? { day: key, sent: 0, delivered: 0, bounced: 0, failed: 0, opened: 0, clicked: 0 });
      }
    }
    return out;
  })();
  const max = Math.max(1, ...rows.map((r) => r.sent + r.failed));

  const t = data.allTime;
  const deliveryRate = t.sent > 0 ? ((t.delivered / t.sent) * 100).toFixed(1) : "—";
  const bounceRate = t.sent > 0 ? ((t.bounced / t.sent) * 100).toFixed(2) : "—";

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Reports</h1>
      <p className="text-sm text-gray-500 mb-6">
        Last 30 days of {data.scope === "workspace" ? "the entire workspace's" : "your"} send activity.
        {data.scope === "workspace" && <span className="ml-1 text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">admin view</span>}
      </p>

      {data.brevoWebhookSeemsUnconfigured && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
          <p className="font-semibold text-amber-900 mb-1">Delivery event webhook isn't configured yet</p>
          <p className="text-amber-800">
            We've handed off emails for delivery, but no delivery / open / click events have come back —
            which means status will stay at "sent" instead of progressing to "delivered". An admin
            needs to wire the delivery provider's outbound webhook to this URL:
          </p>
          <code className="block mt-2 bg-amber-100 px-2 py-1 rounded text-amber-900 break-all">
            https://notify.wenvia.global/api/brevo-events
          </code>
          <p className="text-amber-800 mt-2 text-xs">
            Enable all event types: sent, delivered, hard / soft bounce, spam, blocked, opened, clicked,
            unsubscribed, invalid email. Once saved, future sends will populate this report fully.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Stat label="Sent (all time)" value={t.sent} />
        <Stat label="Delivered" value={`${t.delivered} (${deliveryRate}%)`} />
        <Stat label="Opened" value={t.opened} hint="Recipient's mail client loaded the tracking pixel. Apple Mail and Gmail can pre-fetch this automatically — treat as a floor, not a ceiling." />
        <Stat label="Clicks (engaged - unsub)"
              valueNode={<ClickSplit clicked={t.clicked} unsubscribed={t.unsubscribed} />}
              hint="Click tracking counts the unsubscribe footer link as a click too. We split: green = engaged clicks, red = unsubscribe clicks." />
        <Stat label="Bounced" value={`${t.bounced} (${bounceRate}%)`} tone={t.bounced > 0 ? "warn" : "ok"} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-baseline gap-3">
            <h2 className="font-semibold text-gray-900">
              {interval === "hour" ? "Hourly" : interval === "month" ? "Monthly" : "Daily"} activity
            </h2>
            <div className="inline-flex gap-1 text-xs">
              {(["hour", "day", "month"] as const).map((k) => (
                <button key={k} onClick={() => setInterval(k)}
                  className={`px-2.5 py-1 rounded ${interval === k ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                  {k === "hour" ? "Hourly" : k === "month" ? "Monthly" : "Daily"}
                </button>
              ))}
            </div>
          </div>
          <Legend />
        </div>
        <div className="flex items-end gap-1 h-48">
          {rows.map((r) => {
            const total = r.sent + r.failed;
            const heightPct = total === 0 ? 0 : (total / max) * 100;
            const deliveredPct = total > 0 ? (r.delivered / total) * 100 : 0;
            const bouncedPct = total > 0 ? (r.bounced / total) * 100 : 0;
            const failedPct = total > 0 ? (r.failed / total) * 100 : 0;
            const otherPct = 100 - deliveredPct - bouncedPct - failedPct;
            return (
              // h-full + min-w-0: without h-full the column collapses to
              // content height and the bar's "height: N%" resolves to 0.
              <div key={r.day} className="flex-1 h-full flex flex-col items-center justify-end group relative min-w-0">
                <div className="w-full bg-gray-100 rounded-t" style={{ height: `${Math.max(2, heightPct)}%` }}>
                  <div className="w-full h-full flex flex-col-reverse rounded-t overflow-hidden">
                    {r.delivered > 0 && <div style={{ height: `${deliveredPct}%`, background: "#10b981" }} />}
                    {otherPct > 0 && r.sent > 0 && <div style={{ height: `${otherPct}%`, background: "#a78bfa" }} />}
                    {r.bounced > 0 && <div style={{ height: `${bouncedPct}%`, background: "#f59e0b" }} />}
                    {r.failed > 0 && <div style={{ height: `${failedPct}%`, background: "#ef4444" }} />}
                  </div>
                </div>
                {/* Tooltip on hover */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full mb-1 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none z-10">
                  <div className="font-medium">{formatBucketLabel(r.day, interval)}</div>
                  {r.sent > 0 ? (
                    <>
                      <div>Sent: {r.sent}</div>
                      <div>Delivered: {r.delivered}</div>
                      <div>Bounced: {r.bounced}</div>
                      <div>Opens: {r.opened} · Clicks: {r.clicked}</div>
                    </>
                  ) : <div className="text-gray-400">No sends</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-2">
          <span>{formatBucketLabel(rows[0].day, interval)}</span>
          <span>{formatBucketLabel(rows[rows.length - 1].day, interval)}</span>
        </div>
      </div>

      <ActivityLog />
    </div>
  );
}

function ActivityLog() {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["activity", page],
    queryFn: () => api<{ rows: ActivityRow[]; total: number }>(
      `/api/reports/activity?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
    ),
  });
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="bg-white border border-gray-200 rounded-xl mt-6">
      <div className="px-5 py-3 border-b border-gray-200 flex items-baseline justify-between">
        <h2 className="font-semibold text-gray-900">Recent activity</h2>
        <span className="text-xs text-gray-500">Per-recipient send + event status, newest first</span>
      </div>
      {isLoading && <p className="text-sm text-gray-500 px-5 py-3">Loading…</p>}
      {data && data.rows.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">No activity yet — launch a campaign to see results here.</p>
      )}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-5 py-2">Email</th>
                <th className="text-left px-5 py-2">Campaign</th>
                <th className="text-left px-5 py-2">Sent</th>
                <th className="text-center px-3 py-2">
                  <ColHeaderHint label="Delivered" hint="Recipient mailbox accepted the message." />
                </th>
                <th className="text-center px-3 py-2">
                  <ColHeaderHint label="Opened" hint="Mail client loaded the tracking pixel. Apple Mail / Gmail can pre-fetch this automatically — treat as a floor, not exact." />
                </th>
                <th className="text-center px-3 py-2">
                  <ColHeaderHint label="Clicked" hint="Recipient clicked any link in the body (link redirect tracking). The unsubscribe footer link also counts here — see the Clicks card above for the split." />
                </th>
                <th className="text-center px-3 py-2">
                  <ColHeaderHint label="Bounced" hint="Couldn't be delivered. Hard bounces auto-suppress; soft bounces are temporary." />
                </th>
                <th className="text-center px-3 py-2">
                  <ColHeaderHint label="Unsubscribed" hint="Recipient hit the unsubscribe link (footer or Gmail/Outlook one-click button). Auto-suppressed workspace-wide." />
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.send_job_id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-5 py-2 text-xs">
                    <Link to={`/campaigns/${r.campaign_id}`} className="text-indigo-600 hover:underline">{r.campaign_name}</Link>
                  </td>
                  <td className="px-5 py-2 text-xs text-gray-500">{new Date(r.sent_at).toLocaleString()}</td>
                  <td className="text-center px-3 py-2"><Mark on={r.delivered} good /></td>
                  <td className="text-center px-3 py-2"><Mark on={r.opened} good /></td>
                  <td className="text-center px-3 py-2"><Mark on={r.clicked} good /></td>
                  <td className="text-center px-3 py-2"><Mark on={r.bounced} bad /></td>
                  <td className="text-center px-3 py-2"><Mark on={r.unsubscribed} muted /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <div>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
            <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ColHeaderHint({ label, hint }: { label: string; hint: string }) {
  return (
    <span className="inline-flex items-center gap-1" title={hint}>
      {label}
      <HelpCircle size={11} className="text-gray-400 cursor-help" />
    </span>
  );
}

function Mark({ on, good, bad, muted }: { on: boolean; good?: boolean; bad?: boolean; muted?: boolean }) {
  if (!on) return <span className="text-gray-300">—</span>;
  const cls = bad ? "text-red-600" : muted ? "text-gray-500" : good ? "text-green-600" : "text-gray-700";
  return <Check size={14} className={`inline ${cls}`} aria-label="yes" />;
}

function formatBucketLabel(key: string, interval: Interval): string {
  if (interval === "hour") {
    // 2026-05-10T13:00 → "May 10, 13:00"
    const d = new Date(key + "Z");
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  if (interval === "month") {
    // 2026-05 → "May 2026"
    const d = new Date(key + "-01T00:00:00Z");
    return d.toLocaleString(undefined, { month: "short", year: "numeric" });
  }
  // 2026-05-10 → "May 10"
  const d = new Date(key + "T00:00:00Z");
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}

function Stat({ label, value, valueNode, tone, hint }: {
  label: string; value?: string | number; valueNode?: React.ReactNode; tone?: "ok" | "warn"; hint?: string;
}) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${tone === "warn" ? "border-amber-200" : "border-gray-200"}`}>
      <div className="text-xs text-gray-500 flex items-center gap-1">
        {label}
        {hint && (
          <span title={hint} className="cursor-help text-gray-400 hover:text-gray-600">
            <HelpCircle size={11} />
          </span>
        )}
      </div>
      <div className={`text-xl font-semibold mt-1 ${tone === "warn" ? "text-amber-700" : "text-gray-900"}`}>
        {valueNode ?? value}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded"></span> Delivered</span>
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-violet-400 rounded"></span> Sent (no event yet)</span>
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded"></span> Bounced</span>
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span> Failed</span>
    </div>
  );
}
